/**
 * Pure helpers shared by SettlementDetail and its sub-components. Kept
 * dependency-free (no React, no tRPC) so each helper can be unit-tested
 * in isolation.
 */

import { formatCurrency } from "../../lib/format";
import type { Expense, Member } from "./types";

/**
 * Display name + optional tooltip for a member. When two or more members
 * share the same display name we surface the email as a `title` hint so
 * the picker entries are technically distinguishable on hover, without
 * cluttering the visible text. Use `name` for the on-screen label and
 * `tooltip` for the `title` attribute on the element.
 */
export function labelForMember(
  m: Member,
  all: Member[],
): { name: string; tooltip: string | undefined } {
  const base = m.displayName ?? m.email.split("@")[0] ?? m.email;
  const duplicateCount = all.reduce(
    (n, o) => ((o.displayName ?? o.email.split("@")[0] ?? o.email) === base ? 1 : 0) + n,
    0,
  );
  return { name: base, tooltip: duplicateCount > 1 ? m.email : undefined };
}

/**
 * A split is "custom" when it deviates from the implicit default: equal
 * share across all members for shared expenses, or only-the-payer for
 * private ones. Tolerance covers øre-level rounding from
 * `distributeEqually`.
 */
export function hasCustomSplit(expense: Expense, members: Member[]): boolean {
  const total = Number.parseFloat(expense.amount);
  if (expense.isShared) {
    if (members.length === 0) return false;
    if (expense.splits.length !== members.length) return true;
    const expected = total / members.length;
    const memberIds = new Set(members.map((m) => m.userId));
    for (const s of expense.splits) {
      if (!memberIds.has(s.userId)) return true;
      if (Math.abs(Number.parseFloat(s.share) - expected) > 0.02) return true;
    }
    return false;
  }
  if (expense.splits.length !== 1) return true;
  const sole = expense.splits[0];
  if (!sole || sole.userId !== expense.paidByUserId) return true;
  return Math.abs(Number.parseFloat(sole.share) - total) > 0.005;
}

/**
 * Format a splits-array as a human-readable "Name: kr · Name: kr"
 * string. Used as a tooltip on the splits column in ExpensesTable.
 */
export function describeSplit(
  splits: Expense["splits"],
  memberMap: Map<string, Member>,
  currency: string,
): string {
  return splits
    .map((s) => {
      const m = memberMap.get(s.userId);
      const name = m?.displayName ?? m?.email.split("@")[0] ?? s.userId;
      return `${name}: ${formatCurrency(s.share, currency)}`;
    })
    .join(" · ");
}

/**
 * Case-insensitive substring count — mirrors the auto-categorisation
 * behaviour in `apps/api/src/lib/matching.ts` so the live preview in
 * the smart-categorise dialogs shows exactly the rows that future
 * uploads would match.
 */
export function countPatternMatches(pattern: string, expenses: Expense[]): number {
  const needle = pattern.trim().toUpperCase();
  if (!needle) return 0;
  let n = 0;
  for (const e of expenses) {
    if (e.description.toUpperCase().includes(needle)) n++;
  }
  return n;
}
